import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useWishlist } from "../../hooks/useWishlist";
import { useCart } from "../../hooks/useCart";
import {
  Heart,
  ChevronLeft,
  ChevronRight,
  ShoppingCart,
  Eye,
  Package,
  Truck,
  Shield,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  ProductImageSkeleton,
  ProductThumbnailSkeleton,
  ProductDetailSkeleton,
} from "../common/SkeletonLoader";
import { apiGet, API_BASE_URL } from "../../config/api";

const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { addToCart, cart, updateCartItem, removeFromCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();

  // Simple state management
  const [product, setProduct] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  // Image loading states
  const [mainImageLoading, setMainImageLoading] = useState(true);
  const [thumbnailLoading, setThumbnailLoading] = useState(true);

  // Prevent caching for product detail pages
  useEffect(() => {
    // Add meta tags to prevent caching
    const metaTags = [
      { name: "Cache-Control", content: "no-cache, no-store, must-revalidate" },
      { name: "Pragma", content: "no-cache" },
      { name: "Expires", content: "0" },
    ];

    metaTags.forEach((tag) => {
      let meta = document.querySelector(`meta[name="${tag.name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = tag.name;
        document.head.appendChild(meta);
      }
      meta.content = tag.content;
    });

    // Cleanup function to remove meta tags when component unmounts
    return () => {
      metaTags.forEach((tag) => {
        const meta = document.querySelector(`meta[name="${tag.name}"]`);
        if (meta) {
          meta.remove();
        }
      });
    };
  }, []);

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setLoadError(null);
      setProduct(null);
      try {
        const cacheBuster = Date.now();
        const response = await apiGet(`/products/${slug}?t=${cacheBuster}`, {
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
            Expires: "0",
          },
        });

        if (response.success) {
          setProduct(response.data);

          if (response.data.variants && response.data.variants.length > 0) {
            const variantParam = searchParams.get("variant");
            let defaultVariant =
              response.data.variants.find((v) => v.is_default) ||
              response.data.variants[0];

            if (variantParam) {
              const paramVariant = response.data.variants.find(
                (v) => v.id === variantParam || v.sku === variantParam
              );
              if (paramVariant) defaultVariant = paramVariant;
            }

            setSelectedVariant(defaultVariant);
          }
        } else {
          setLoadError("Product not found");
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        const status = error.response?.status;
        if (status === 404) {
          setLoadError("Product not found");
        } else {
          setLoadError(
            error.response?.data?.message || "Failed to load product"
          );
        }
        toast.error("Failed to load product");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug, searchParams]);

  // Simple helper functions
  const getImageUrl = (imageUrl) => {
    if (!imageUrl)
      return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect width='400' height='400' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='20' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";

    if (imageUrl.startsWith("http")) return imageUrl;

    let cleanUrl = imageUrl
      .replace(/^\/uploads\//, "")
      .replace(/^uploads\//, "");
    const base = (API_BASE_URL || "").replace(/\/+$/, "");
    return `${base}/uploads/${cleanUrl}`;
  };

  const getCurrentImages = () => {
    if (!product) return [];
    if (selectedVariant?.images?.length > 0) return selectedVariant.images;
    return product.images || [];
  };

  const handleVariantChange = (variant) => {
    setSelectedVariant(variant);
    setSelectedImageIndex(0);
    // Reset quantity to 1 when variant changes
    setQuantity(1);
  };

  // Compute weight in grams from available fields/attributes
  const getWeightInGrams = (variant) => {
    if (!variant) return null;
    // 1) Direct numeric field in grams
    if (
      typeof variant.weight_grams === "number" &&
      !Number.isNaN(variant.weight_grams)
    ) {
      return Math.round(variant.weight_grams);
    }
    if (
      typeof variant.weight_grams === "string" &&
      variant.weight_grams.trim() !== ""
    ) {
      const parsed = parseFloat(variant.weight_grams);
      if (!Number.isNaN(parsed)) return Math.round(parsed);
    }

    // 2) Try to infer from attributes (e.g., "100g", "250 gr", "0.5 kg")
    const attributes = Array.isArray(variant.attributes)
      ? variant.attributes
      : [];
    const weightAttr = attributes.find((attr) => {
      const name = (attr?.name || "").toString().toLowerCase();
      const value = (attr?.value || "").toString().toLowerCase();
      return (
        name.includes("weight") ||
        name.includes("gram") ||
        name.includes("kg") ||
        /\b(\d+[\d.,]*)\s*(g|gr|grams|kg)\b/.test(value)
      );
    });

    if (weightAttr && weightAttr.value) {
      const raw = weightAttr.value.toString().toLowerCase().trim();
      const match = raw.match(/([\d.,]+)\s*(kg|g|gr|grams)?/i);
      if (match) {
        const num = parseFloat(match[1].replace(/,/g, ""));
        const unit = match[2] ? match[2].toLowerCase() : "g";
        if (!Number.isNaN(num)) {
          if (unit.includes("kg")) return Math.round(num * 1000);
          // treat g, gr, grams the same
          return Math.round(num);
        }
      }
    }

    return null;
  };

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    const minQuantity = 1;
    const maxQuantity = Math.min(
      selectedVariant?.stock ?? 99,
      99
    );

    if (newQuantity >= minQuantity && newQuantity <= maxQuantity) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedVariant || quantity < 1) return;

    try {
      const currentCartQuantity = getCartQuantity(selectedVariant.id);

      if (currentCartQuantity > 0) {
        // Find the cart item ID for this variant
        const cartItem = cart.items.find(
          (item) => item.variant_id === selectedVariant.id
        );
        if (cartItem) {
          // Update existing item in cart
          await updateCartItem(cartItem.id, currentCartQuantity + quantity);
          toast.success("Cart updated!");
        } else {
          // Add new item to cart
          await addToCart(selectedVariant.id, quantity);
          toast.success("Added to cart!");
        }
      } else {
        // Add new item to cart
        await addToCart(selectedVariant.id, quantity);
        toast.success("Added to cart!");
      }

      // Reset quantity to 1 after adding to cart
      setQuantity(1);
    } catch (error) {
      toast.error("Failed to update cart");
    }
  };

  // Get current quantity of variant in cart
  const getCartQuantity = (variantId) => {
    if (!variantId || !cart?.items) return 0;
    const cartItem = cart.items.find((item) => item.variant_id === variantId);
    return cartItem ? cartItem.quantity : 0;
  };

  // Remove variant from cart
  const handleRemoveFromCart = async () => {
    if (!selectedVariant) return;

    try {
      // Find the cart item ID for this variant
      const cartItem = cart.items.find(
        (item) => item.variant_id === selectedVariant.id
      );
      if (cartItem) {
        console.log("Removing cart item from cart:", cartItem.id);
        await removeFromCart(cartItem.id);
        setQuantity(1); // Reset local quantity state
        toast.success("Removed from cart!");
      } else {
        toast.error("Item not found in cart");
      }
    } catch (error) {
      console.error("Error removing from cart:", error);
      toast.error("Failed to remove from cart");
    }
  };

  const handleToggleWishlist = async () => {
    if (!user) {
      toast.error("Please log in to manage wishlist");
      return;
    }

    try {
      await toggleWishlist(product.id);
    } catch (error) {
      toast.error("Failed to update wishlist");
    }
  };

  const openImageModal = (index) => {
    setModalImageIndex(index);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
  };

  const navigateModalImage = (direction) => {
    const currentImages = getCurrentImages();
    if (direction === "next") {
      setModalImageIndex(
        modalImageIndex === currentImages.length - 1 ? 0 : modalImageIndex + 1
      );
    } else {
      setModalImageIndex(
        modalImageIndex === 0 ? currentImages.length - 1 : modalImageIndex - 1
      );
    }
  };

  // Early returns for loading and error states
  if (loading && !product) {
    return (
      <div className="min-h-screen bg-white">
        <div className="bg-gray-50 border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="h-4 bg-gray-200 rounded w-48 animate-pulse"></div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-4">
              <ProductImageSkeleton />
              <div className="grid grid-cols-4 gap-3">
                {[...Array(4)].map((_, i) => (
                  <ProductThumbnailSkeleton key={i} />
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <ProductDetailSkeleton />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadError || !product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            {loadError === "Product not found"
              ? "Product not found"
              : "Unable to load product"}
          </h1>
          <p className="text-gray-600 mb-6">
            {loadError === "Product not found"
              ? "This product may have been removed or the link is incorrect."
              : loadError || "Something went wrong. Please try again."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              Try again
            </button>
            <button
              onClick={() => navigate("/shop")}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back to shop
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentImages = getCurrentImages();
  const variantStock = selectedVariant?.stock ?? 0;
  const maxQuantity = Math.min(variantStock, 99);
  const canAddToCart =
    quantity >= 1 &&
    selectedVariant &&
    variantStock > 0 &&
    quantity <= variantStock;
  const currentPrice =
    selectedVariant?.price_sale || selectedVariant?.price_regular || 0;
  const originalPrice = selectedVariant?.price_regular || 0;
  const hasDiscount =
    selectedVariant?.price_sale &&
    selectedVariant?.price_sale < selectedVariant?.price_regular;

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex items-center space-x-2 text-sm">
            <button
              onClick={() => navigate("/")}
              className="text-gray-500 hover:text-gray-700"
            >
              Home
            </button>
            <ChevronRight size={14} className="text-gray-400" />
            <button
              onClick={() => navigate("/shop")}
              className="text-gray-500 hover:text-gray-700"
            >
              Shop
            </button>
            <ChevronRight size={14} className="text-gray-400" />
            <span className="text-gray-900 font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Side - Image Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="aspect-square bg-gray-50 rounded-xl overflow-hidden relative group border border-gray-200">
              {currentImages.length > 0 ? (
                <>
                  <div
                    className="w-full h-full cursor-zoom-in"
                    onClick={() => openImageModal(selectedImageIndex)}
                  >
                    {mainImageLoading && <ProductImageSkeleton />}
                    <img
                      src={getImageUrl(currentImages[selectedImageIndex]?.url)}
                      alt={
                        currentImages[selectedImageIndex]?.alt_text ||
                        product.name
                      }
                      className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${
                        mainImageLoading ? "hidden" : ""
                      }`}
                      onLoad={() => setMainImageLoading(false)}
                      onError={(e) => {
                        e.target.src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='500' height='500' viewBox='0 0 500 500'%3E%3Crect width='500' height='500' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='16' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
                        setMainImageLoading(false);
                      }}
                    />
                  </div>

                  {/* Navigation Arrows */}
                  {currentImages.length > 1 && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImageIndex(
                            selectedImageIndex === 0
                              ? currentImages.length - 1
                              : selectedImageIndex - 1
                          );
                        }}
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 opacity-0 group-hover:opacity-100 transition-all duration-200"
                      >
                        <ChevronLeft size={18} className="text-gray-700" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImageIndex(
                            selectedImageIndex === currentImages.length - 1
                              ? 0
                              : selectedImageIndex + 1
                          );
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 opacity-0 group-hover:opacity-100 transition-all duration-200"
                      >
                        <ChevronRight size={18} className="text-gray-700" />
                      </button>
                    </>
                  )}

                  {/* Zoom Icon */}
                  <button
                    onClick={() => openImageModal(selectedImageIndex)}
                    className="absolute top-3 right-3 bg-white/90 hover:bg-white p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    title="Click to zoom"
                  >
                    <Eye size={16} className="text-gray-700" />
                  </button>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-gray-400">No image available</span>
                </div>
              )}
            </div>

            {/* Thumbnail Images */}
            {currentImages.length > 1 && (
              <div className="grid grid-cols-4 gap-3">
                {currentImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`aspect-square overflow-hidden border-2 transition-all duration-200 ${
                      selectedImageIndex === index
                        ? "border-blue-400 ring-2 ring-blue-100"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {thumbnailLoading && <ProductThumbnailSkeleton />}
                    <img
                      src={getImageUrl(image.url)}
                      alt={image.alt_text || `${product.name} ${index + 1}`}
                      className={`w-full h-full object-cover ${
                        thumbnailLoading ? "hidden" : ""
                      }`}
                      onLoad={() => setThumbnailLoading(false)}
                      onError={(e) => {
                        e.target.src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
                        setThumbnailLoading(false);
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right Side - Product Details */}
          <div className="space-y-6">
            {/* Brand */}
            {product.brand && (
              <div className="text-sm text-blue-600 font-medium uppercase tracking-wide">
                {product.brand}
              </div>
            )}

            {/* Product Title & Wishlist */}
            <div className="flex items-start justify-between">
              <h1 className="text-3xl font-extrabold text-gray-900 leading-tight pr-4 tracking-tight">
                {product.name}
              </h1>
              <button
                onClick={handleToggleWishlist}
                className="p-2 rounded-full hover:bg-primary-50 transition-colors group"
              >
                <Heart
                  size={24}
                  className={`transition-colors ${
                    isInWishlist(product.id)
                      ? "text-red-500 fill-current"
                      : "text-gray-400 group-hover:text-primary-600"
                  }`}
                  fill={isInWishlist(product.id) ? "currentColor" : "none"}
                />
              </button>
            </div>

            {/* Variant Selector - Desktop */}
            <div className="hidden md:block">
              {product.variants && product.variants.length > 1 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-lg mb-3">
                    Available Variations
                  </h3>
                  <div className="grid grid-cols-6 gap-1">
                    {product.variants.map((variant) => {
                      const variantImage =
                        (variant.images &&
                          variant.images.find((img) => img.is_primary)) ||
                        (variant.images && variant.images[0]) ||
                        null;
                      const variantId = variant.id || variant.sku;

                      return (
                        <button
                          key={variantId}
                          onClick={() => handleVariantChange(variant)}
                          disabled={variant.stock <= 0}
                          className={`flex flex-col items-center bg-white border rounded-md transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-400 group hover:shadow-md ${
                            selectedVariant?.id === variant.id
                              ? "border-black border-2"
                              : "border-gray-300 border"
                          } ${
                            variant.stock <= 0
                              ? "opacity-60 cursor-not-allowed"
                              : ""
                          }`}
                          style={{
                            width: "4.5rem",
                            height: "4.5rem",
                            padding: 0,
                          }}
                        >
                          <div className="w-16 h-16 flex items-center justify-center overflow-hidden rounded bg-white relative">
                            {variantImage ? (
                              <img
                                src={getImageUrl(variantImage.url)}
                                alt={
                                  variantImage?.alt_text ||
                                  `Variant ${
                                    variant.sku || variant.code || variant.id
                                  }`
                                }
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src =
                                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
                                }}
                              />
                            ) : (
                              // No image available - show placeholder
                              <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                <span className="text-gray-400 text-xs">
                                  No Image
                                </span>
                              </div>
                            )}
                            {variant.stock <= 0 && (
                              <div className="absolute inset-0 bg-red-800 bg-opacity-70 rounded" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Price with MRP */}
            <div className="space-y-2">
              <div className="flex items-baseline space-x-3">
                {hasDiscount ? (
                  <div className="flex flex-col">
                    <span className="text-xl text-gray-400 line-through">
                      MRP ₹{originalPrice}
                    </span>
                    <span className="text-3xl font-extrabold text-gray-900">
                      ₹{currentPrice}
                    </span>
                  </div>
                ) : (
                  <span className="text-3xl font-extrabold text-gray-900">
                    MRP ₹{currentPrice}
                  </span>
                )}
                {hasDiscount && (
                  <span className="bg-red-100 text-red-600 px-2 py-1 text-sm font-semibold">
                    {Math.round(
                      ((originalPrice - currentPrice) / originalPrice) * 100
                    )}
                    % OFF
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">Inclusive of all taxes</p>
            </div>

            {/* Product Specifications - Desktop */}
            <div className="bg-gray-50 p-4 space-y-3 hidden md:block">
              <h3 className="font-semibold text-gray-900">Product Details</h3>
              <div className="grid grid-cols-1 gap-2 text-sm">
                {selectedVariant?.attributes?.map((attr, index) => (
                  <div key={index} className="flex justify-between">
                    <span className="text-gray-600 capitalize">
                      {attr.name}:
                    </span>
                    <span className="font-medium text-gray-900">
                      {attr.value}
                    </span>
                  </div>
                ))}
                {getWeightInGrams(selectedVariant) !== null && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Weight:</span>
                    <span className="font-medium text-gray-900">
                      {getWeightInGrams(selectedVariant)}g
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">SKU:</span>
                  <span className="font-medium text-gray-900">
                    {selectedVariant?.sku}
                  </span>
                </div>
              </div>
            </div>

            {/* Quantity & Add to Cart */}
            <div className="border-t pt-6 space-y-4">
              {/* Quantity Selector */}
              <div className="flex items-center justify-between">
                <span className="text-gray-700 font-medium">Quantity:</span>
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <button
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                    className="p-3 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-l-lg"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="px-6 py-3 font-medium min-w-[4rem] text-center bg-gray-50">
                    {quantity}
                  </span>
                  <button
                    onClick={() => handleQuantityChange(1)}
                    disabled={quantity >= maxQuantity}
                    className="p-3 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-r-lg"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                disabled={!canAddToCart}
                className="w-full bg-black text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
              >
                <ShoppingCart size={20} />
                <span>Add to Cart</span>
              </button>

              {/* Cart Status */}
              {getCartQuantity(selectedVariant?.id) > 0 && (
                <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <ShoppingCart size={18} className="text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">
                      {getCartQuantity(selectedVariant?.id)} in cart
                    </span>
                  </div>
                  <button
                    onClick={handleRemoveFromCart}
                    className="text-red-600 hover:text-red-700 text-sm font-medium transition-colors"
                  >
                    Remove
                  </button>
                </div>
              )}
              {/* Product Specifications - Mobile */}
              <div className="bg-gray-50 p-4 space-y-3 block md:hidden">
                <h3 className="font-semibold text-gray-900">Product Details</h3>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  {selectedVariant?.attributes?.map((attr, index) => (
                    <div key={index} className="flex justify-between">
                      <span className="text-gray-600 capitalize">
                        {attr.name}:
                      </span>
                      <span className="font-medium text-gray-900">
                        {attr.value}
                      </span>
                    </div>
                  ))}
                  {getWeightInGrams(selectedVariant) !== null && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Weight:</span>
                      <span className="font-medium text-gray-900">
                        {getWeightInGrams(selectedVariant)}g
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">SKU:</span>
                    <span className="font-medium text-gray-900">
                      {selectedVariant?.sku}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            {/* Mobile: show variant selector here */}
            <div className="block md:hidden">
              {product.variants && product.variants.length > 1 && (
                <div>
                  <div className="grid grid-cols-5 gap-1">
                    {product.variants.map((variant) => {
                      const variantImage =
                        (variant.images &&
                          variant.images.find((img) => img.is_primary)) ||
                        (variant.images && variant.images[0]) ||
                        null;
                      const variantId = variant.id || variant.sku;

                      return (
                        <button
                          key={variantId}
                          onClick={() => handleVariantChange(variant)}
                          className={`flex flex-col items-center bg-white border rounded-md transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-400 group hover:shadow-md ${
                            selectedVariant?.id === variant.id
                              ? "border-black border-2"
                              : "border-gray-300 border"
                          } ${
                            variant.stock <= 0
                              ? "opacity-60 cursor-not-allowed"
                              : ""
                          }`}
                          style={{
                            width: "3.6rem",
                            height: "3.6rem",
                            padding: 0,
                          }}
                          disabled={variant.stock <= 0}
                        >
                          <div className="w-14 h-14 flex items-center justify-center overflow-hidden rounded bg-white relative">
                            {variantImage ? (
                              <img
                                src={getImageUrl(variantImage.url)}
                                alt={
                                  variantImage?.alt_text ||
                                  `Variant ${
                                    variant.sku || variant.code || variant.id
                                  }`
                                }
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src =
                                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
                                }}
                              />
                            ) : (
                              // No image available - show placeholder
                              <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                <span className="text-gray-400 text-xs">
                                  No Image
                                </span>
                              </div>
                            )}
                            {variant.stock <= 0 && (
                              <div className="absolute inset-0 bg-red-800 bg-opacity-70 rounded" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {product.description && (
              <div className="border-t pt-6">
                <h3 className="font-semibold text-gray-900 mb-3">
                  Description
                </h3>
                <div
                  className="product-description-html font-modern text-gray-800 leading-relaxed text-base md:text-lg prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
              </div>
            )}

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-4 border-t">
              <div className="text-center">
                <Truck className="mx-auto mb-2 text-blue-600" size={24} />
                <div className="text-xs font-medium text-gray-900">
                  Free Shipping
                </div>
                <div className="text-xs text-gray-500">
                  On orders over ₹2500
                </div>
              </div>
              <div className="text-center">
                <Shield className="mx-auto mb-2 text-green-600" size={24} />
                <div className="text-xs font-medium text-gray-900">
                  Secure Payment
                </div>
                <div className="text-xs text-gray-500">100% protected</div>
              </div>
              <div className="text-center">
                <Package className="mx-auto mb-2 text-purple-600" size={24} />
                <div className="text-xs font-medium text-gray-900">
                  Quality Assured
                </div>
                <div className="text-xs text-gray-500">Premium materials</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Zoom Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <button
              onClick={closeImageModal}
              className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 rounded-full p-3 z-10 transition-colors"
            >
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                className="text-white"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Navigation Arrows */}
            {currentImages.length > 1 && (
              <>
                <button
                  onClick={() => navigateModalImage("prev")}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 rounded-full p-3 z-10 transition-colors"
                >
                  <ChevronLeft size={24} className="text-white" />
                </button>
                <button
                  onClick={() => navigateModalImage("next")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 rounded-full p-3 z-10 transition-colors"
                >
                  <ChevronRight size={24} className="text-white" />
                </button>
              </>
            )}

            {/* Main Modal Image */}
            <div className="relative max-w-full max-h-full">
              <img
                src={getImageUrl(currentImages[modalImageIndex]?.url)}
                alt={currentImages[modalImageIndex]?.alt_text || product.name}
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.target.src =
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='800' viewBox='0 0 800 800'%3E%3Crect width='800' height='800' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='40' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
                }}
              />
            </div>

            {/* Image Counter */}
            {currentImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/20 rounded-full px-4 py-2">
                <span className="text-white text-sm font-medium">
                  {modalImageIndex + 1} / {currentImages.length}
                </span>
              </div>
            )}

            {/* Thumbnail Strip */}
            {currentImages.length > 1 && (
              <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex space-x-2 max-w-full overflow-x-auto px-4">
                {currentImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setModalImageIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      modalImageIndex === index
                        ? "border-white"
                        : "border-transparent opacity-60 hover:opacity-80"
                    }`}
                  >
                    <img
                      src={getImageUrl(image.url)}
                      alt={image.alt_text || `${product.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='6' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Click outside to close */}
          <div className="absolute inset-0 -z-10" onClick={closeImageModal} />
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
